import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Box,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { useUser, useUserDispatch, useAuthDispatch } from '@/providers';
import { SET_USER, CLEAR_SESSION, LOGOUT } from '@/actions/types';
import ProfileForm from './ProfileForm';
import TopupForm from '../billing/method/TopupForm';
import { deleteUser } from '../../actions/user';
import { fetchJobs } from '../../actions/jobs';
import { fetchPaymentMethods } from '../../actions/paymentMethods';
import CredentialsForm from '../credentials/CredentialsForm';
import DefaultSettingsForm from './settings/DefaultSettingsForm';
import ApiKeysForm from './settings/ApiKeysForm';

export default function AccountSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useUser();
  const userDispatch = useUserDispatch();
  const authDispatch = useAuthDispatch();
  const jobs = useSelector((state) => state.jobs || []);
  const paymentMethods = useSelector((state) => state.paymentMethods);
  const primaryPaymentMethod = _.find(
    paymentMethods,
    (method) => method.id === user.primary_pm,
  );

  const [pressedDelete, setPressedDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.selected_project) {
      dispatch(fetchJobs(user.selected_project));
    }
    if (!user?.billing_account_id) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, user]);

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await deleteUser();
      // Clear user context
      userDispatch({ type: SET_USER, payload: null });
      // Clear auth session
      authDispatch({ type: CLEAR_SESSION });
      authDispatch({ type: LOGOUT });
      // Sign out globally from AWS Amplify
      try {
        await signOut({ global: true });
        // Small delay to ensure sign out completes
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (signOutError) {
        // Even if signOut fails, continue with navigation
        console.error('Error signing out:', signOutError);
      }
      // Navigate to login page
      navigate('/');
    } catch (error) {
      console.error('Error deleting user:', error);
      setLoading(false);
    }
  };

  return (
    <Container>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h4' align='center' gutterBottom>
            Profile Information
          </Typography>
          <ProfileForm edit={true} />
        </Box>

        <Paper elevation={2} sx={{ p: 3 }}>
          <CredentialsForm project_uuid={user.id} />
        </Paper>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant='h4' align='center' gutterBottom>
            API Keys
          </Typography>
          <ApiKeysForm />
        </Paper>

        {!user.billing_account_id && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <TopupForm
              primaryPaymentMethod={primaryPaymentMethod}
              user={user}
            />
          </Paper>
        )}

        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant='h4' align='center' gutterBottom>
            Default Job Settings
          </Typography>
          <DefaultSettingsForm />
        </Paper>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Button
            variant='contained'
            color='error'
            disabled={jobs.length !== 0}
            onClick={() => setPressedDelete(true)}
          >
            Delete User
          </Button>
          {jobs.length !== 0 && (
            <Alert severity='error' sx={{ mt: 2 }}>
              You must terminate all jobs to enable this function.
            </Alert>
          )}
        </Paper>
      </Stack>

      <Dialog open={pressedDelete} onClose={() => setPressedDelete(false)}>
        <DialogTitle>Delete Your Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your account? This action cannot be
            undone. If you have a credit balance, all credits will be forfeited
            upon account deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPressedDelete(false)} disabled={loading}>
            No
          </Button>
          <Button color='error' onClick={handleDeleteUser} loading={loading}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
